import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ProfitOrderDto } from "@hisense/shared";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { TableShell, Td, Th, Tr } from "@/components/ui/Table";
import { formatDate } from "@/domain/datetime";
import { formatQty, formatUnitPrice } from "@/domain/money";
import { translateStatus } from "@/domain/i18n";
import { amount, marginClass } from "./profitDisplay";

export function CostBadge({
  status,
}: {
  status: ProfitOrderDto["costStatus"];
}) {
  const { t } = useTranslation();
  return (
    <Badge
      tone={
        status === "missing" ? "warn" : status === "po" ? "info" : "neutral"
      }
    >
      {t(`profitWorkspace.costStatus.${status}`)}
    </Badge>
  );
}

export function ProfitDetail({
  order,
  generatedAt,
  onClose,
}: {
  order: ProfitOrderDto;
  generatedAt: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const issueText = (issue: string) =>
    t(`profitWorkspace.issues.${issue}`, {
      defaultValue: t("profitWorkspace.unresolvedIssue"),
    });
  return (
    <Modal
      title={order.piNo}
      eyebrow={t("profitWorkspace.costDetail")}
      closeLabel={t("common.close")}
      onClose={onClose}
      className="max-w-6xl"
    >
      <div className="overflow-y-auto p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-3">
          <Link
            className="font-mono text-brand underline underline-offset-4"
            to={`/proforma-invoices/${order.piId}`}
          >
            {t("profitWorkspace.openPi")}
          </Link>
          <span className="text-sm text-ink-soft">
            {order.customerName || "—"} · {formatDate(order.date)}
          </span>
          <CostBadge status={order.costStatus} />
        </div>
        <p className="mt-3 text-xs text-ink-soft">
          {t("profitWorkspace.basis")}
        </p>
        <p className="mt-1 text-xs text-ink-faint">
          {t("profitWorkspace.generatedAt", {
            at: new Date(generatedAt).toLocaleString(),
          })}
        </p>
        <dl className="my-4 grid grid-cols-1 gap-3 rounded-md border border-line bg-sunken p-4 sm:grid-cols-3">
          <div>
            <dt className="text-xs text-ink-soft">
              {t("profitWorkspace.revenue")}
            </dt>
            <dd className="mt-1 font-mono tabular">
              {amount(order.revenue, order.currency)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-soft">
              {t("profitWorkspace.cost")}
            </dt>
            <dd className="mt-1 font-mono tabular">
              {amount(order.cost, order.currency)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-soft">
              {t("profitWorkspace.margin")}
            </dt>
            <dd
              className={`mt-1 font-mono tabular ${marginClass(order.margin)}`}
            >
              {amount(order.margin, order.currency)}
              {order.marginPct != null ? ` · ${order.marginPct}%` : ""}
            </dd>
          </div>
        </dl>
        {order.cost == null && (
          <p className="mb-4 rounded-sm border border-warn/30 bg-warn-tint p-3 text-sm text-ink-soft">
            {t("profitWorkspace.missingExplanation", {
              known: amount(order.knownCost, order.currency),
            })}
          </p>
        )}
        {order.issues.length > 0 && (
          <ul className="mb-4 list-inside list-disc text-xs text-warn">
            {order.issues.map((issue) => (
              <li key={issue}>{issueText(issue)}</li>
            ))}
          </ul>
        )}
        <div className="space-y-5">
          {order.items.map((item, index) => (
            <section
              key={item.piItemId ?? `${item.code}-${index}`}
              className="overflow-hidden rounded-md border border-line"
            >
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line bg-sunken p-3">
                <div>
                  <h3 className="font-mono text-sm font-semibold">
                    {index + 1}. {item.code || "—"}
                    {item.subRef ? ` / ${item.subRef}` : ""}
                  </h3>
                  <p className="mt-1 text-sm text-ink-soft">
                    {item.description}
                  </p>
                </div>
                <div className="text-right font-mono tabular text-sm">
                  <p>
                    {formatQty(item.quantity)} {item.unit}
                  </p>
                  <p className="mt-1">{amount(item.cost, order.currency)}</p>
                </div>
              </div>
              <div className="p-3">
                <p className="mb-3 font-mono tabular text-xs text-ink-soft">
                  {t("profitWorkspace.quantityCoverage", {
                    po: formatQty(item.poQuantity),
                    catalog: formatQty(item.catalogQuantity),
                    missing: formatQty(item.missingQuantity),
                    unit: item.unit,
                  })}
                </p>
                {item.issues.length > 0 && (
                  <ul className="mb-3 list-inside list-disc text-xs text-warn">
                    {item.issues.map((issue) => (
                      <li key={issue}>{issueText(issue)}</li>
                    ))}
                  </ul>
                )}
                {item.sources.length ? (
                  <TableShell>
                    <thead>
                      <tr>
                        <Th>{t("profitWorkspace.source")}</Th>
                        <Th align="right">{t("common.quantity")}</Th>
                        <Th align="right">{t("profitWorkspace.unitPrice")}</Th>
                        <Th align="right">{t("profitWorkspace.cost")}</Th>
                        <Th>{t("profitWorkspace.capturedAt")}</Th>
                        <Th>{t("profitWorkspace.sourceUpdatedAt")}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.sources.map((source, sourceIndex) => (
                        <Tr key={`${source.kind}-${source.id}-${sourceIndex}`}>
                          <Td>
                            <div className="py-2">
                              {source.kind === "po" ? (
                                <Link
                                  className="font-mono text-brand underline underline-offset-4"
                                  to={`/purchase-orders/${source.id}`}
                                >
                                  {source.label}
                                </Link>
                              ) : (
                                <span>
                                  {t("profitWorkspace.catalogCapture")}
                                </span>
                              )}
                              {source.poStatus && (
                                <span className="ml-2 text-xs text-ink-soft">
                                  {translateStatus(t, source.poStatus)}
                                </span>
                              )}
                              {source.legacyBaseline && (
                                <p className="mt-1 max-w-72 whitespace-normal text-xs text-warn">
                                  {t("profitWorkspace.legacyBaseline")}
                                </p>
                              )}
                            </div>
                          </Td>
                          <Td align="right" className="font-mono tabular">
                            {formatQty(source.quantity)} {source.unit}
                          </Td>
                          <Td align="right" className="font-mono tabular">
                            {formatUnitPrice(
                              source.unitPrice,
                              source.currency,
                              false,
                            )}{" "}
                            {source.currency}
                          </Td>
                          <Td align="right" className="font-mono tabular">
                            {amount(source.cost, source.currency)}
                          </Td>
                          <Td className="text-xs text-ink-soft">
                            {source.capturedAt == null
                              ? "—"
                              : new Date(source.capturedAt).toLocaleString()}
                          </Td>
                          <Td className="text-xs text-ink-soft">
                            {source.updatedAt == null
                              ? "—"
                              : new Date(source.updatedAt).toLocaleString()}
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </TableShell>
                ) : (
                  <p className="text-sm text-ink-faint">
                    {t("profitWorkspace.noCostSource")}
                  </p>
                )}
              </div>
            </section>
          ))}
        </div>
        {order.poRefs.length > 0 && (
          <section className="mt-5 rounded-md border border-line p-3">
            <h3 className="text-sm font-semibold">
              {t("profitWorkspace.linkedPos")}
            </h3>
            <p className="my-2 text-xs text-ink-soft">
              {t("profitWorkspace.poExcluded")}
            </p>
            <div className="flex flex-wrap gap-3">
              {order.poRefs.map((po) => (
                <div key={po.id} className="rounded-sm border border-line p-3 text-xs">
                  <Link to={`/purchase-orders/${po.id}`} className="font-mono text-sm text-brand underline underline-offset-4">{po.poNo}</Link>
                  <p className="mt-1">{translateStatus(t,po.status)} · <span className="font-mono">{po.currency}</span></p>
                  <p className="mt-1 text-ink-soft">{t("common.shipping")}: {amount(po.shipping,po.currency)} · {t("purchaseOrders.tax")}: {amount(po.tax,po.currency)}</p>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </Modal>
  );
}
